<template>
  <div class="table-pagination">
    <div class="table-pagination__meta">
      <div class="table-pagination__per-page">
        <label
          :for="selectId"
          class="table-pagination__per-page-label"
        >Rows per page:</label>
        <select
          :id="selectId"
          class="table-pagination__per-page-select"
          :value="Number(perPage)"
          @change="onPerPageChange"
        >
          <option
            v-for="option in resolvedPerPageOptions"
            :key="option"
            :value="option"
          >
            {{ option }}
          </option>
        </select>
      </div>

      <div class="table-pagination__info">
        {{ recordInfo }}
      </div>
    </div>

    <div class="table-pagination__controls">
      <b-pagination
        :value="value"
        :total-rows="totalRows"
        :per-page="perPage"
        first-number
        last-number
        :align="align"
        :prev-class="prevClass"
        :next-class="nextClass"
        class="table-pagination__list mt-0 mb-0"
        @input="$emit('input', $event)"
      >
        <template #prev-text>
          <slot name="prev-text">
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </slot>
        </template>
        <template #next-text>
          <slot name="next-text">
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </slot>
        </template>
      </b-pagination>

      <div class="table-pagination__nav">
        <button
          type="button"
          class="page-link table-pagination__nav-btn"
          :disabled="value <= 1"
          aria-label="Go to previous page"
          @click="goPrev"
        >
          <feather-icon
            icon="ChevronLeftIcon"
            size="18"
          />
          <span>Previous</span>
        </button>
        <button
          type="button"
          class="page-link table-pagination__nav-btn"
          :disabled="value >= totalPages"
          aria-label="Go to next page"
          @click="goNext"
        >
          <span>Next</span>
          <feather-icon
            icon="ChevronRightIcon"
            size="18"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { BPagination } from 'bootstrap-vue'

const DEFAULT_PER_PAGE_OPTIONS = [10, 20, 30, 40, 50]

export default {
  name: 'TablePagination',
  components: {
    BPagination,
  },
  props: {
    value: {
      type: Number,
      default: 1,
    },
    totalRows: {
      type: [Number, String],
      default: 0,
    },
    perPage: {
      type: [Number, String],
      default: 10,
    },
    perPageOptions: {
      type: Array,
      default: () => [...DEFAULT_PER_PAGE_OPTIONS],
    },
    align: {
      type: String,
      default: 'right',
    },
    prevClass: {
      type: String,
      default: 'prev-item',
    },
    nextClass: {
      type: String,
      default: 'next-item',
    },
  },
  emits: ['input', 'per-page-change'],
  data() {
    return {
      selectId: `table-pagination-rpp-${Math.floor(Math.random() * Date.now())}`,
    }
  },
  computed: {
    total() {
      return Number(this.totalRows) || 0
    },
    currentPerPage() {
      return Number(this.perPage) || 1
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.currentPerPage))
    },
    firstRecord() {
      if (this.total === 0) {
        return 0
      }
      return ((this.value - 1) * this.currentPerPage) + 1
    },
    lastRecord() {
      return Math.min(this.total, this.value * this.currentPerPage)
    },
    recordInfo() {
      return `${this.firstRecord} - ${this.lastRecord} of ${this.total}`
    },
    resolvedPerPageOptions() {
      const options = [...this.perPageOptions]
      const current = this.currentPerPage
      if (current && !options.includes(current)) {
        options.unshift(current)
      }
      return options
    },
  },
  methods: {
    goPrev() {
      if (this.value > 1) {
        this.$emit('input', this.value - 1)
      }
    },
    goNext() {
      if (this.value < this.totalPages) {
        this.$emit('input', this.value + 1)
      }
    },
    onPerPageChange(event) {
      const next = Number(event.target.value)
      if (!next || next === this.currentPerPage) {
        return
      }
      this.$emit('per-page-change', next)
      this.$emit('input', 1)
    },
  },
}
</script>

<style lang="scss">
.table-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  width: 100%;
  padding: 0.75rem 10px 20px;
}

.table-pagination__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.25rem;
}

.table-pagination__per-page {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.table-pagination__per-page-label {
  margin: 0;
  font-size: 0.95rem;
  color: #6e6b7b;
}

.table-pagination__per-page-select {
  appearance: none;
  background-color: transparent;
  border: 0;
  border-radius: 0;
  color: #6e6b7b;
  font-weight: 600;
  font-size: 0.95rem;
  height: auto;
  margin: 0;
  padding: 0 1rem 0 0.25rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%236e6b7b' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right center;
  background-size: 8px 5px;
}

.table-pagination__per-page-select:focus {
  outline: none;
}

.table-pagination__info {
  font-size: 0.95rem;
  color: #6e6b7b;
  white-space: nowrap;
}

.table-pagination__controls {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: auto;
}

.table-pagination__nav {
  display: none;
}

@media (max-width: 767.98px) {
  .table-pagination {
    flex-direction: column;
    align-items: center;
  }

  .table-pagination__meta {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .table-pagination__controls {
    align-items: center;
    margin-left: 0;
    width: 100%;
  }

  /* Hide page numbers on small screens — only Previous / Next */
  .table-pagination__list {
    display: none !important;
  }

  .table-pagination__nav {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    width: 100%;
  }

  .table-pagination__nav-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    min-height: 2.5rem;
    padding: 0.35rem 0.85rem;
    margin: 0;
    border-radius: 5rem;
    white-space: nowrap;
  }

  .table-pagination__nav-btn:disabled {
    opacity: 0.55;
    pointer-events: none;
  }
}
</style>
